import type { PartialBlock } from '@blocknote/core';

type InlineNode = {
  type?: string;
  href?: string;
  content?: Array<{ text?: string }>;
};

function isLocalNonMdFileHref(href: string): boolean {
  const decoded = decodeURIComponent(href);
  if (/^https?:\/\//i.test(decoded) || decoded.startsWith('data:')) {
    return false;
  }
  // Rewritten Notion import links become in-app routes, not downloadable files.
  if (decoded.startsWith('/page/') || decoded.startsWith('/database/')) {
    return false;
  }
  // Page and database exports are links, not attachments.
  if (/\.(md|csv)$/i.test(decoded.split(/[?#]/)[0] ?? decoded)) {
    return false;
  }
  return true;
}

function linkLabel(node: InlineNode): string {
  if (!Array.isArray(node.content)) {
    return '';
  }
  return node.content.map((part) => part.text ?? '').join('');
}

function paragraphToFileBlock(block: PartialBlock): PartialBlock | null {
  if (block.type !== 'paragraph' || !Array.isArray(block.content) || block.content.length !== 1) {
    return null;
  }

  const node = block.content[0] as InlineNode;
  if (node.type !== 'link' || !node.href || !isLocalNonMdFileHref(node.href)) {
    return null;
  }

  const name =
    linkLabel(node) ||
    decodeURIComponent(node.href.split('/').pop() ?? node.href) ||
    'file';

  return {
    id: block.id,
    type: 'file',
    props: { name, url: node.href },
    children: block.children ?? [],
  } as PartialBlock;
}

/** Turn standalone local attachment links into file blocks (e.g. Notion PDF exports). */
export function convertLocalFileLinksToFileBlocks(blocks: PartialBlock[]): PartialBlock[] {
  return blocks.map((block) => {
    const converted = paragraphToFileBlock(block);
    if (converted) {
      return {
        ...converted,
        children: convertLocalFileLinksToFileBlocks(
          (converted.children ?? []) as PartialBlock[],
        ),
      };
    }

    if (block.children?.length) {
      return {
        ...block,
        children: convertLocalFileLinksToFileBlocks(block.children as PartialBlock[]),
      };
    }

    return block;
  });
}
